import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  LayoutTemplate, Braces, Wrench, ExternalLink,
  BookOpen, File, Workflow, Cloud, Star,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import { imagekitWidth } from "@/lib/imagekit";
import { API_BASE_URL } from "@/lib/apiBase";

const TYPE_ICONS = {
  tool: Wrench, template: LayoutTemplate, prompt: Braces,
  guide: BookOpen, file: File, automation: Workflow, saas_resource: Cloud,
};

const LABELS = {
  ar: {
    type: { tool: "أداة", template: "قالب", prompt: "برومبت", guide: "دليل", file: "ملف", automation: "أتمتة", saas_resource: "مورد SaaS" },
    access: { free: "مجاني", paid: "مدفوع", requires_signup: "يتطلب تسجيل" },
    featured: "مميز",
    empty: "ستجد الموارد هنا عند نشرها. يمكنك الآن استكشاف المشاريع أو مراسلتي حول احتياجك.",
  },
  en: {
    type: { tool: "Tool", template: "Template", prompt: "Prompt", guide: "Guide", file: "File", automation: "Automation", saas_resource: "SaaS Resource" },
    access: { free: "Free", paid: "Paid", requires_signup: "Requires signup" },
    featured: "Featured",
    empty: "Resources will appear here when published. For now, explore the projects or tell me what you need.",
  },
  fr: {
    type: { tool: "Outil", template: "Template", prompt: "Prompt", guide: "Guide", file: "Fichier", automation: "Automatisation", saas_resource: "Ressource SaaS" },
    access: { free: "Gratuit", paid: "Payant", requires_signup: "Inscription requise" },
    featured: "En avant",
    empty: "Les ressources apparaîtront ici dès leur publication. En attendant, découvrez les projets ou parlez-moi de votre besoin.",
  },
};

const iconWrapCls = "flex size-14 items-center justify-center rounded-2xl bg-white/80 text-sage-deep";
const typeChipCls = "rounded-full bg-mist px-3 py-1 text-xs font-semibold text-graphite";
const accessChipCls = "rounded-full border border-line px-3 py-1 text-xs font-medium text-graphite/60";
const cardCls = "interactive-card flex flex-col rounded-[1.75rem] border border-line bg-white p-4 shadow-card";
const bodyCls = "flex flex-1 flex-col px-3 pb-2 pt-5";
const ctaCls =
  "mt-5 inline-flex h-10 w-fit items-center gap-2 rounded-full border border-charcoal/15 px-5 text-sm font-semibold text-charcoal transition-colors duration-200 hover:border-sage hover:text-sage-deep";

const RemoteCard = ({ resource: r, index: i, labels, lang, reduce, openLabel }) => {
  const Icon = TYPE_ICONS[r.type] || Wrench;
  const title = lang === "ar" ? r.title_ar : r.title_en;
  const desc = lang === "ar" ? r.desc_ar : r.desc_en;
  return (
    <motion.article
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`resource-card-${i}`}
      className={cardCls}
    >
      {r.thumbnail ? (
        <img src={imagekitWidth(r.thumbnail, 640)} alt={title} loading="lazy" className="h-40 w-full rounded-[1.25rem] object-cover" />
      ) : (
        <div className="bg-dots relative flex h-40 items-center justify-center overflow-hidden rounded-[1.25rem] bg-mist" aria-hidden="true">
          <span className={iconWrapCls}>
            <Icon className="size-6" strokeWidth={1.6} />
          </span>
        </div>
      )}
      <div className={bodyCls}>
        <div className="flex flex-wrap items-center gap-2">
          <span className={typeChipCls}>{labels.type[r.type] || r.type}</span>
          <span className={accessChipCls}>{labels.access[r.access] || r.access}</span>
          {r.featured && (
            <span data-testid={`resource-featured-badge-${i}`} className="flex items-center gap-1 rounded-full bg-sage px-3 py-1 text-xs font-semibold text-white">
              <Star className="size-3" />
              {labels.featured}
            </span>
          )}
        </div>
        <h3 className="mt-3.5 text-lg font-bold text-charcoal">{title}</h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-graphite/70">{desc}</p>
        {r.external_url ? (
          <a href={r.external_url} target="_blank" rel="noopener noreferrer" data-testid={`resource-open-cta-${i}`} className={ctaCls}>
            {openLabel}
            <ExternalLink className="size-4 rtl:-scale-x-100" />
          </a>
        ) : (
          <button type="button" onClick={() => toast(labels.linkSoon || "")} data-testid={`resource-open-cta-${i}`} className={ctaCls}>
            {openLabel}
            <ExternalLink className="size-4 rtl:-scale-x-100" />
          </button>
        )}
      </div>
    </motion.article>
  );
};

const Resources = () => {
  const { t, lang } = useLanguage();
  const reduce = useReducedMotion();
  const [remote, setRemote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/resources`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((d) => {
        if (!cancelled) setRemote(Array.isArray(d.resources) ? d.resources : []);
      })
      .catch(() => {
        if (!cancelled) setRemote([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const labels = LABELS[lang];

  return (
    <section id="resources" data-testid="resources-section" className="scroll-mt-28 border-y border-line bg-mist/35 py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          eyebrow={t.resources.eyebrow}
          title={t.resources.title}
          sub={t.resources.sub}
          testId="resources-heading"
        />

        {remote === null ? (
          <p className="mt-12 text-center text-sm text-graphite/60" aria-live="polite">{lang === "ar" ? "جارٍ تحميل الملحقات…" : lang === "fr" ? "Chargement des ressources…" : "Loading resources…"}</p>
        ) : remote.length === 0 ? (
          <p className="mt-8 text-center text-sm leading-7 text-graphite/70">{labels.empty}</p>
        ) : (
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {remote.map((r, i) => (
              <RemoteCard key={r.id} resource={r} index={i} labels={labels} lang={lang} reduce={reduce} openLabel={t.resources.openCta} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Resources;
